/** Dev Agent chat only — `[ARTIFACT: art_xxx]` → fenced blocks for Streamdown.
 *  Not used by `components/organisms/chat` (legacy product chat).
 *
 *  Backend `to_artifact_markdown` emits markers instead of raw code. Bodies
 *  land in `devAgentStore.artifactsById` via SSE `artifact_complete` / hydrate.
 */
import type { BackendArtifact } from "@/stores/devAgentStore";

const ARTIFACT_MARKER = /\[ARTIFACT:\s*(art_[a-f0-9]+)\s*\]/g;
const BARE_ARTIFACT_ID = /\bart_[a-f0-9]+\b/g;
const BARE_ARTIFACT_ID_CHECK = /\bart_[a-f0-9]+\b/;

const TYPE_TO_LANG: Record<string, string> = {
  apex_class: "apex",
  apex_trigger: "apex",
  visualforce_page: "html",
  lwc_bundle: "javascript",
  aura_bundle: "javascript",
  soql_result: "json",
  impact_graph: "text",
  test_result: "text",
};

function langFor(art: BackendArtifact): string {
  return art.language || TYPE_TO_LANG[art.type] || "text";
}

function metadataTypeFor(art: BackendArtifact): string {
  const explicit = art.metadata?.metadata_type;
  if (typeof explicit === "string" && explicit) return explicit;
  if (art.type === "apex_trigger") return "ApexTrigger";
  if (art.type === "visualforce_page") return "ApexPage";
  if (art.type === "lwc_bundle") return "LightningComponentBundle";
  if (art.type === "aura_bundle") return "AuraDefinitionBundle";
  const language = (art.language || "").toLowerCase();
  if (["apextrigger", "trigger"].includes(language)) return "ApexTrigger";
  if (["visualforce"].includes(language)) return "ApexPage";
  if (["aura"].includes(language)) return "AuraDefinitionBundle";
  if (["lwc", "html", "javascript", "typescript", "jsx", "tsx", "react", "css"].includes(language)) {
    return "LightningComponentBundle";
  }
  return "ApexClass";
}

function fenceMetaFor(art: BackendArtifact): string {
  const meta = art.metadata || {};
  const metadataId =
    (typeof meta.metadata_id === "string" && meta.metadata_id) ||
    (typeof meta.source_id === "string" && meta.source_id) ||
    (typeof meta.original_id === "string" && meta.original_id) ||
    (typeof meta.changes?.metadata_id === "string" && meta.changes.metadata_id) ||
    "";
  const parts = [
    `artifact_id=${encodeURIComponent(art.id)}`,
    `artifact_name=${encodeURIComponent(art.name || "")}`,
    `metadata_type=${encodeURIComponent(metadataTypeFor(art))}`,
    `operation=${encodeURIComponent(
      typeof meta.operation === "string" && meta.operation ? meta.operation : "read",
    )}`,
  ];
  if (metadataId) parts.push(`metadata_id=${encodeURIComponent(metadataId)}`);
  return parts.join(" ");
}

function _fenceFor(content: string): string {
  let longest = 2;
  const matches = content.match(/`+/g);
  if (matches) {
    for (const m of matches) if (m.length > longest) longest = m.length;
  }
  return "`".repeat(longest + 1);
}

function artifactToFence(art: BackendArtifact): string {
  const fence = _fenceFor(art.content);
  return `\n\n${fence}${langFor(art)} ${fenceMetaFor(art)}\n${art.content}\n${fence}\n`;
}

function loadingPlaceholder(id: string): string {
  return `\n\n> Generating code artifact \`${id}\`...\n`;
}

export function hasArtifactReference(content: string): boolean {
  return content.includes("[ARTIFACT:") || BARE_ARTIFACT_ID_CHECK.test(content);
}

export function rewriteArtifactMarkers(
  content: string,
  artifactsById: Record<string, BackendArtifact>,
): string {
  if (!hasArtifactReference(content)) return content;

  const protectedFences: string[] = [];
  const protect = (match: string) => {
    protectedFences.push(match);
    return `\u0000A${protectedFences.length - 1}\u0000`;
  };

  let rewritten = content.replace(ARTIFACT_MARKER, (_match, id: string) => {
    const art = artifactsById[id];
    if (process.env.NODE_ENV === "development") {
      console.debug("[dev-agent artifactMarkers]", {
        marker: id,
        source: "marker",
        found: !!art,
        hasContent: typeof art?.content === "string" && art.content.length > 0,
      });
    }
    if (!art?.content) return loadingPlaceholder(id);
    return protect(artifactToFence(art));
  });

  rewritten = rewritten.replace(BARE_ARTIFACT_ID, (id: string) => {
    const art = artifactsById[id];
    if (process.env.NODE_ENV === "development") {
      console.debug("[dev-agent artifactMarkers]", {
        marker: id,
        source: "bare",
        found: !!art,
        hasContent: typeof art?.content === "string" && art.content.length > 0,
      });
    }
    if (!art?.content) return loadingPlaceholder(id);
    return protect(artifactToFence(art));
  });

  return protectedFences.reduce(
    (acc, fence, idx) => acc.replace(`\u0000A${idx}\u0000`, fence),
    rewritten,
  );
}
